import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import type { CarBrandList, CarModelList, CarSubTypeList, CarTypeList } from 'entities/car';
import { apiClient } from 'shared/api';
import CloseIcon from 'shared/assets/icons/close.svg';
import { validateINN, ValidationRegular } from 'shared/model/constants/validations';
import { ButtonCustom } from 'shared/ui/button-custom';
import { CheckboxInput } from 'shared/ui/checkbox-input';
import { FileInputCustom } from 'shared/ui/file-input-custom';
import { PhoneInput } from 'shared/ui/phone-input';
import { SelectInput } from 'shared/ui/select-input';
import { TextInput } from 'shared/ui/text-input';

interface EditApplicationModalProps {
  open: boolean;
  onClose: () => void;
  appData: any;
  type: string | undefined;
  onSave: () => Promise<void>;
  // Справочники
  typeValueList: CarTypeList;
  subTypeValueList: CarSubTypeList;
  brandValueList: CarBrandList;
  modelValueList: CarModelList;
  // Функции загрузки справочников
  onLoadSubtypes: (typeId: number | string) => Promise<void>;
  onLoadBrandsForType: (typeId: number | string) => Promise<void>;
  onLoadBrandsForSubtype: (subtypeId: number | string) => Promise<void>;
  onLoadModels: (brandId: number | string, subtypeId: number | string) => Promise<void>;
}

export const EditApplicationModal: React.FC<EditApplicationModalProps> = ({
  open,
  onClose,
  appData,
  type,
  onSave,
  typeValueList,
  subTypeValueList,
  brandValueList,
  modelValueList,
  onLoadSubtypes,
  onLoadBrandsForType,
  onLoadBrandsForSubtype,
  onLoadModels,
}) => {
  const [loading, setLoading] = useState(false);

  // Форма редактирования - основные поля
  const [inn, setInn] = useState<string | number>('');
  const [bio, setBio] = useState<string | number>('');
  const [email, setEmail] = useState<string | number>('');
  const [phone, setPhone] = useState<string | undefined>('');
  const [quantity, setQuantity] = useState<string | number>('');
  const [paymentV, setPayment] = useState<string>('');

  // Поля для лизинговых заявок
  const [price, setPrice] = useState<string | number>('');
  const [initialPayment, setInitialPayment] = useState<string | number>('');
  const [leasePeriod, setLeasePeriod] = useState<string | number>('');
  const [paymentType, setPaymentType] = useState<string>('');

  // Автомобильные поля
  const [carType, setCarType] = useState<number | string>('');
  const [carSubtype, setCarSubtype] = useState<number | string>('');
  const [brand, setBrand] = useState<number | string>('');
  const [model, setModel] = useState<number | string>('');

  // Другие поля
  const [carTypeOtherBlock, showCarTypeOtherBlock] = useState(false);
  const [otherCarType, setOtherCarType] = useState('');
  const [carSubtypeOtherBlock, showCarSubtypeOtherBlock] = useState(false);
  const [otherSubType, setOtherSubType] = useState('');
  const [carBrandOtherBlock, showCarBrandOtherBlock] = useState(false);
  const [otherBrand, setOtherBrand] = useState('');
  const [carModelOtherBlock, showCarModelOtherBlock] = useState(false);
  const [otherModel, setOtherModel] = useState('');
  const [kp, setKp] = useState<any>();
  const [selectedValues, setSelectedValues] = useState<{ [key: string]: string | number }>({});

  // Ошибки валидации
  const [bioErr, setBioErr] = useState(false);
  const [innErr, setInnErr] = useState(false);
  const [conditionErr, setConditionErr] = useState(false);
  const [emailErr, setEmailErr] = useState(false);
  const [phoneErr, setPhoneErr] = useState(false);
  const [quantityErr, setQuantityErr] = useState(false);
  const [carBrandErr, setCarBrandErr] = useState(false);
  const [carModelErr, setCarModelErr] = useState(false);
  const [carTypeErr, setCarTypeErr] = useState(false);
  const [carSubtypeErr, setCarSubtypeErr] = useState(false);
  const [paymentVariantErr, setPaymentVariantErr] = useState(false);
  const [priceErr, setPriceErr] = useState(false);
  const [destination, setDestination] = useState<string>('');
  const [destinationErr, setDestinationErr] = useState(false);
  const [currency, setCurrency] = useState<string>('');
  const [currencyErr, setCurrencyErr] = useState(false);
  const [initialPaymentErr, setInitialPaymentErr] = useState(false);
  const [leasePeriodErr, setLeasePeriodErr] = useState(false);
  const [paymentTypeErr, setPaymentTypeErr] = useState(false);

  type PaymentType = {
    name: string;
    value: string;
  };

  const paymentTypesList: PaymentType[] = [
    { name: 'Аннуитетный', value: 'annuity' },
    { name: 'Дифференцированный', value: 'differential' },
    { name: 'Сезонный', value: 'seasonal' },
    { name: 'Индивидуальный', value: 'individual' },
  ];


  // Вспомогательные функции
  const getIdByName = (name: string | undefined, items: any) => {
    const item = items.find((item: any) => item.name === name);
    return item ? item.id : null;
  };

  // Флаг для отслеживания начального заполнения
  const [isFillingForm, setIsFillingForm] = useState(false);

  // Функция для заполнения основных полей формы
  const fillBasicFields = () => {
    if (!appData) return;

    setInn(appData.inn || '');
    setBio(appData.bio || '');
    setEmail(appData.email || '');
    setPhone(appData.phone || '');
    setQuantity(appData.quantity || '');
    setPayment(appData.pay_method || '');
    setDestination(appData.destination || '');
    setCurrency(appData.currency || '');

    // Заполняем лизинговые поля
    if (type === 'leasing') {
      setPrice(appData.price || '');
      setInitialPayment(appData.initial_payment || '');
      setLeasePeriod(appData.lease_period || '');
      if (appData.payment_type) {
        const value =
          paymentTypesList.find(
            (item) => item.name.toLowerCase() === appData.payment_type.toLowerCase(),
          )?.value || '';
        setPaymentType(value);
      }
    }

    // Заполняем параметры автомобиля
    if (appData.car_params && Array.isArray(appData.car_params)) {
      const selectedVals: { [key: string]: string | number } = {};
      appData.car_params.forEach((param: any) => {
        if (param.fieldName && param.fieldValue) {
          selectedVals[param.fieldName] = param.fieldValue[0];
        }
      });
      setSelectedValues(selectedVals);
    }
  };

  // Заполняем форму при открытии модального окна
  useEffect(() => {
    if (open && appData && !isFillingForm) {
      setIsFillingForm(true);
      fillBasicFields();

      // Заполняем тип автомобиля
      if (appData.car_type_other) {
        showCarTypeOtherBlock(true);
        setOtherCarType(appData.car_type_other);
        setIsFillingForm(false);
      } else if (appData.car_type && typeValueList.length > 0) {
        const typeId = getIdByName(appData.car_type, typeValueList);
        if (typeId) {
          setCarType(typeId);
          onLoadSubtypes(typeId);
          onLoadBrandsForType(typeId);
        } else {
          showCarTypeOtherBlock(true);
          setOtherCarType(appData.car_type);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appData?.id, typeValueList.length]);

  // Заполняем подтип после загрузки списка подтипов
  useEffect(() => {
    if (open && appData && carType && subTypeValueList.length > 0 && !carSubtype && isFillingForm) {
      if (appData.car_subtype_other) {
        showCarSubtypeOtherBlock(true);
        setOtherSubType(appData.car_subtype_other);
        setIsFillingForm(false);
      } else if (appData.car_subtype) {
        const subtypeId = getIdByName(appData.car_subtype, subTypeValueList);
        if (subtypeId) {
          setCarSubtype(subtypeId);
          onLoadBrandsForSubtype(subtypeId);
        } else {
          showCarSubtypeOtherBlock(true);
          setOtherSubType(appData.car_subtype);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appData?.id, carType, subTypeValueList.length, carSubtype, isFillingForm]);

  // Заполняем марку после загрузки списка марок
  useEffect(() => {
    if (open && appData && carSubtype && brandValueList.length > 0 && !brand && isFillingForm) {
      if (appData.car_brand_other) {
        showCarBrandOtherBlock(true);
        setOtherBrand(appData.car_brand_other);
        setIsFillingForm(false);
      } else if (appData.car_brand) {
        const brandId = getIdByName(appData.car_brand, brandValueList);
        if (brandId) {
          setBrand(brandId);
          onLoadModels(brandId, carSubtype);
        } else {
          showCarBrandOtherBlock(true);
          setOtherBrand(appData.car_brand);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appData?.id, carSubtype, brandValueList.length, brand, isFillingForm]);

  // Заполняем модель после загрузки списка моделей
  useEffect(() => {
    if (open && appData && brand && modelValueList.length > 0 && !model && isFillingForm) {
      if (appData.car_model_other) {
        showCarModelOtherBlock(true);
        setOtherModel(appData.car_model_other);
      } else if (appData.vehicle) {
        const modelId = getIdByName(appData.vehicle, modelValueList);
        if (modelId) {
          setModel(modelId);
        } else {
          showCarModelOtherBlock(true);
          setOtherModel(appData.vehicle);
        }
      }
      setIsFillingForm(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appData?.id, brand, modelValueList.length, model, isFillingForm]);

  // Сбрасываем флаг при закрытии модального окна
  useEffect(() => {
    if (!open) {
      setIsFillingForm(false);
      setCarType('');
      setCarSubtype('');
      setBrand('');
      setModel('');
      showCarTypeOtherBlock(false);
      showCarSubtypeOtherBlock(false);
      showCarBrandOtherBlock(false);
      showCarModelOtherBlock(false);
    }
  }, [open]);

  // Обработчики изменений полей формы
  const handleChangeInn = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInn(value || '');
    const validInn = validateINN(value);
    setInnErr(!validInn);
  };

  const handleChangeBio = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setBio(value);
    const bioValid: boolean = !(
      value?.toString().length && ValidationRegular.BIO.test(value?.toString())
    );
    setBioErr(bioValid);
  };

  const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEmail(value);
    const emailValid: boolean = !(
      value?.toString().length && ValidationRegular.MAIL.test(value?.toString())
    );
    setEmailErr(emailValid);
  };

  const handleChangePhone = (value: string) => {
    let formatted = value.replace(/[^\d+]/g, '');
    if (formatted.startsWith('8')) {
      formatted = '+7' + formatted.slice(1);
    }
    if (!formatted.startsWith('+7')) {
      formatted = '+7';
    }
    const digitsOnly = formatted.replace(/\D/g, '');
    if (digitsOnly.length > 11) {
      formatted = '+' + digitsOnly.slice(0, 11);
    }
    setPhone(formatted);
    const isValid = /^\+7\d{10}$/.test(formatted);
    setPhoneErr(!isValid);
  };

  const handleChangeQuantity = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQuantity(value);
    const quantityValid: boolean = !(
      value?.toString().length && ValidationRegular.QUANTITY.test(value?.toString())
    );
    setQuantityErr(quantityValid);
  };

  const handleChangePayment = (event: SelectChangeEvent<unknown>) => {
    setPayment(event.target.value as string);
    setPaymentVariantErr(false);
  };

  const handleChangeCarType = async (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value;
    const carTypeItem = typeValueList.find((item) => item.id === value);
    setCarType(value as number);
    setBrand('');
    setModel('');
    setCarSubtype('');
    setCarTypeErr(false);
    setCarSubtypeErr(false);

    if (carTypeItem?.value === 'other') {
      showCarTypeOtherBlock(true);
    } else {
      setOtherCarType('');
      showCarTypeOtherBlock(false);
      await onLoadBrandsForType(value as number);
      await onLoadSubtypes(value as number);
    }
  };

  const handleChangeCarSubtype = async (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value;
    const carSubtypeItem = subTypeValueList?.find((item) => item.id === value);

    setCarSubtype(value as number);
    setBrand('');
    setModel('');
    setCarSubtypeErr(false);

    if (carSubtypeItem?.value === 'other') {
      showCarSubtypeOtherBlock(true);
    } else {
      setOtherSubType('');
      showCarSubtypeOtherBlock(false);
      await onLoadBrandsForSubtype(value as number);
    }
  };

  const handleChangeBrand = async (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value;
    const brandItem = brandValueList.find((item) => item.id === value);
    setBrand(event.target.value as number);
    setModel('');
    setCarBrandErr(false);

    if (brandItem?.value === 'other') {
      showCarBrandOtherBlock(true);
    } else {
      setOtherBrand('');
      showCarBrandOtherBlock(false);
      await onLoadModels(value as number, carSubtype as number);
    }
  };

  const handleChangeModel = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as number;
    const modelItem = modelValueList.find((item) => item.id === value);
    setModel(value);
    setCarModelErr(false);

    if (modelItem?.value === 'other') {
      showCarModelOtherBlock(true);
    } else {
      setOtherModel('');
      showCarModelOtherBlock(false);
    }
  };

  const handleChangeKp = (event: any) => {
    setKp(event.target.files);
  };

  const handleChangeOtherCarType = (event: ChangeEvent<HTMLInputElement>) => {
    setOtherCarType(event.target.value);
  };

  const handleChangeOtherSubType = (event: ChangeEvent<HTMLInputElement>) => {
    setOtherSubType(event.target.value);
  };

  const handleChangeOtherBrand = (event: ChangeEvent<HTMLInputElement>) => {
    setOtherBrand(event.target.value);
  };

  const handleChangeOtherModel = (event: ChangeEvent<HTMLInputElement>) => {
    setOtherModel(event.target.value);
  };

  const handleParamChange = (fieldName: string, value: string | number) => {
    setSelectedValues((prevState) => ({
      ...prevState,
      [fieldName]: value,
    }));
  };

  const handleChangePrice = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPrice(value);
    setPriceErr(!value || isNaN(Number(value)));
  };

  const handleChangeInitialPayment = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInitialPayment(value);
    setInitialPaymentErr(!value || isNaN(Number(value)));
  };

  const handleChangeLeasePeriod = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLeasePeriod(value);
    setLeasePeriodErr(!value || isNaN(Number(value)));
  };

  const handleChangePaymentType = (event: SelectChangeEvent<unknown>) => {
    setPaymentType(event.target.value as string);
    setPaymentTypeErr(false);
  };

  const handleChangeDestination = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setDestination(value);
    setDestinationErr(!value?.trim());
  };

  const handleChangeCurrency = (event: SelectChangeEvent<unknown>) => {
    setCurrency(event.target.value as string);
    setCurrencyErr(false);
  };

  // Функция сохранения изменений
  const handleSaveChanges = async () => {
    const formData = new FormData();

    // Добавляем ID заявки для обновления
    formData.append('id', appData.id.toString());

    let carParamsArray = [];
    if (selectedValues && Object.keys(selectedValues).length > 0) {
      carParamsArray = Object.entries(selectedValues).map(([key, value]) => ({
        fieldName: key,
        fieldValue: value,
      }));
      formData.append('car_params', JSON.stringify(carParamsArray));
    }

    // Добавляем параметр fulfilled для обозначения завершенной заявки
    formData.append('fulfilled', 'true');

    // Валидация и добавление полей
    if (email?.toString().length && ValidationRegular.MAIL.test(email?.toString())) {
      formData.append('email', email?.toString() || '');
    } else {
      setEmailErr(true);
      return;
    }

    if (phone?.toString().length) {
      formData.append('phone', phone?.toString() || '');
    } else {
      setPhoneErr(true);
      return;
    }

    if (bio?.toString().length && ValidationRegular.BIO.test(bio?.toString())) {
      formData.append('bio', bio?.toString() || '');
    } else {
      setBioErr(true);
      return;
    }

    if (validateINN(inn?.toString())) {
      formData.append('inn', inn?.toString() || '');
      setInnErr(false);
    } else {
      setInnErr(true);
      return;
    }

    if (quantity?.toString().length && ValidationRegular.QUANTITY.test(quantity?.toString())) {
      formData.append('quantity', quantity?.toString() || '');
    } else {
      setQuantityErr(true);
      return;
    }

    if (currency?.toString().trim()) {
      formData.append('currency', currency.toString().trim());
      setCurrencyErr(false);
    } else {
      setCurrencyErr(true);
      return;
    }

    formData.append('type', type || 'select');

    if (type === 'select') {
      if (destination?.toString().trim()) {
        formData.append('destination', destination.toString().trim());
        setDestinationErr(false);
      } else {
        setDestinationErr(true);
        return;
      }

      if (paymentV.length > 2) {
        formData.append('pay_method', paymentV);
      } else {
        setPaymentVariantErr(true);
        return;
      }
    } else if (type === 'leasing') {
      // Лизинговые поля
      if (price && !isNaN(Number(price))) {
        formData.append('price', price.toString());
      } else {
        setPriceErr(true);
        return;
      }

      if (initialPayment && !isNaN(Number(initialPayment))) {
        formData.append('initial_payment', initialPayment.toString());
      } else {
        setInitialPaymentErr(true);
        return;
      }

      if (leasePeriod && !isNaN(Number(leasePeriod))) {
        formData.append('lease_period', leasePeriod.toString());
      } else {
        setLeasePeriodErr(true);
        return;
      }

      if (paymentType) {
        const value = paymentTypesList.find((item) => item.value === paymentType);
        formData.append('payment_type', value?.name || paymentType);

      } else {
        setPaymentTypeErr(true);
        return;
      }
    }

    // Обработка автомобильных полей
    if (carTypeOtherBlock) {
      if (otherCarType) {
        formData.append('car_type_other', otherCarType);
        formData.append('car_subtype_other', '-');
        formData.append('car_brand_other', '-');
        formData.append('car_model_other', '-');
      } else {
        setCarTypeErr(true);
        return;
      }
    } else {
      if (carType) {
        formData.append('car_type', carType.toString());
      } else {
        setCarTypeErr(true);
        return;
      }

      if (carSubtypeOtherBlock) {
        if (otherSubType) {
          formData.append('car_subtype_other', otherSubType);
          formData.append('car_brand_other', '-');
          formData.append('car_model_other', '-');
        } else {
          setCarSubtypeErr(true);
          return;
        }
      } else {
        if (carSubtype.toString()) {
          formData.append('car_subtype', carSubtype.toString());
        } else {
          setCarSubtypeErr(true);
          return;
        }

        if (carBrandOtherBlock) {
          if (otherBrand) {
            formData.append('car_brand_other', otherBrand);
            formData.append('car_model_other', '-');
          } else {
            setCarBrandErr(true);
            return;
          }
        } else {
          if (brand.toString()) {
            formData.append('car_brand', brand.toString());
          } else {
            setCarBrandErr(true);
            return;
          }

          if (carModelOtherBlock) {
            if (otherModel) {
              formData.append('car_model_other', otherModel);
            } else {
              setCarModelErr(true);
              return;
            }
          } else {
            if (model.toString()) {
              formData.append('vehicle', model.toString());
            } else {
              setCarModelErr(true);
              return;
            }
          }
        }
      }
    }

    if (!appData.fulfilled) {
      formData.append('fulfilled', 'true');
    }

    if (kp && kp[0]) {
      formData.append('kp', kp[0]);
    }

    try {
      setLoading(true);

      if (!appData.fulfilled) {
        const url = `${type === 'select' ? '/select-applications' : '/leasing-applications'}/confirm-draft`;
        await apiClient.post(url, formData);
      }

      const url = `${type === 'select' ? '/select-applications' : '/leasing-applications'}/${appData.fulfilled ? 'update' : 'new'}`;
      await apiClient.post(url, formData);

      // Вызываем callback для обновления данных
      await onSave();
      onClose();

      // Показываем уведомление об успехе
      alert('Заявка успешно обновлена!');
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Ошибка при обновлении заявки:', error);
      alert('Произошла ошибка при обновлении заявки');
    } finally {
      setLoading(false);
    }
  };

  // Компоненты формы редактирования
  const CarTypeBlock = () => {
    return (
      <Box sx={{ marginBottom: 2 }}>
        <span>Выберите тип ТС</span>
        <SelectInput
          name={'car_type'}
          value={carType}
          required
          error={carTypeErr}
          onChange={handleChangeCarType}
          placeholder={'Выберите тип ТС'}
        >
          {typeValueList?.map((item: { id: number; name: string; value: string }) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </SelectInput>
      </Box>
    );
  };

  const CarSubTypeBlock = () => {
    return (
      <Box sx={{ marginBottom: 2 }}>
        <span>Выберите подтип ТС</span>
        <SelectInput
          value={carSubtype}
          name={'car_subtype'}
          onChange={handleChangeCarSubtype}
          placeholder={'Выберите подтип ТС'}
          disabled={!carType}
          required
          error={carSubtypeErr}
        >
          {subTypeValueList.map((item: { id: number; name: string }) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </SelectInput>
      </Box>
    );
  };

  const CarBrandBlock = () => {
    return (
      <Box sx={{ marginBottom: 2 }}>
        <span>Выберите марку</span>
        <SelectInput
          disabled={!carSubtype}
          value={brand}
          required
          error={carBrandErr}
          onChange={handleChangeBrand}
          placeholder={'Выберите марку'}
        >
          {brandValueList.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </SelectInput>
      </Box>
    );
  };

  const CarModelBlock = () => {
    return (
      <Box sx={{ marginBottom: 2 }}>
        <span>Выберите модель</span>
        <SelectInput
          disabled={!brand}
          value={model}
          required
          error={carModelErr}
          onChange={handleChangeModel}
          placeholder={'Выберите модель'}
        >
          {modelValueList.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </SelectInput>
      </Box>
    );
  };

  const CarParamsBlock = () => {
    const vehicleModel = modelValueList.find((item) => item.id === model);

    return (
      <>
        {vehicleModel?.car_params?.map((item: any) => (
          <Box key={item.fieldName} sx={{ marginBottom: 2 }}>
            {item.fieldName}
            <SelectInput
              value={selectedValues[item.fieldName] || ''}
              onChange={(event) => handleParamChange(item.fieldName, event.target.value as number)}
              placeholder={item.fieldName}
            >
              {item.fieldValue?.map((value: any, index: number) => (
                <MenuItem key={`${item.fieldName}-${index}`} value={value.toString()}>
                  {value}
                </MenuItem>
              ))}
            </SelectInput>
          </Box>
        ))}
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      sx={{
        '.MuiDialog-paper': {
          padding: '30px',
          borderRadius: '10px',
          maxHeight: '90vh',
          overflow: 'auto',
        },
      }}
    >
      <DialogTitle sx={{ padding: 0, marginBottom: 3, position: 'relative' }}>
        <span style={{ fontSize: '24px', fontWeight: 600 }}>
          Редактирование {appData.fulfilled ? 'заявки' : 'черновика'} № {appData?.id}
        </span>
        <IconButton
          disableRipple
          aria-label='close'
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            fontSize: 18,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Основные поля */}
          <PhoneInput
            onChange={handleChangePhone}
            value={phone || ''}
            error={phoneErr}
            name={'phone'}
            required
            placeholder={'Введите телефон'}
          />

          <TextInput
            name={'inn'}
            type={'number'}
            error={innErr}
            onChange={handleChangeInn}
            value={inn || ''}
            placeholder={'Введите ИНН покупателя/лизингополучателя'}
            required
          />

          <TextInput
            onChange={handleChangeEmail}
            value={email || ''}
            error={emailErr}
            name={'email'}
            type={'email'}
            required
            placeholder={'Введите E-mail'}
          />

          <TextInput
            onChange={handleChangeBio}
            value={bio || ''}
            error={bioErr}
            name={'bio'}
            type={'text'}
            placeholder={'Введите ФИО представителя покупателя/лизингополучателя'}
            required
          />
          {type === 'select' && (
            <>
              {/* Состояние */}
              <Box
                sx={{
                  gap: 2,
                  paddingLeft: 2,
                }}
              >
                <span>Состояние</span>
                <br />
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    border: conditionErr ? '1px solid red' : 'none',
                    borderRadius: 1,
                    padding: 1,
                  }}
                >
                  <FormControlLabel
                    control={
                      <CheckboxInput
                        defaultChecked={appData?.condition?.includes('Новый')}
                        name={'condition-new'}
                      />
                    }
                    label='Новый'
                  />
                  <FormControlLabel
                    control={
                      <CheckboxInput
                        defaultChecked={appData?.condition?.includes('С пробегом')}
                        name={'condition-old'}
                      />
                    }
                    label='С пробегом'
                  />
                </Box>
              </Box>

              <Box>
                <span>Введите место поставки ТС</span>
                <TextInput
                  onChange={handleChangeDestination}
                  value={destination || ''}
                  error={destinationErr}
                  required
                  name={'destination'}
                  type={'text'}
                  placeholder={'Введите место поставки ТС'}
                />
              </Box>
            </>
          )}

          <Box>
            <span>Выберите валюту</span>
            <SelectInput
              name={'currency'}
              value={currency}
              onChange={handleChangeCurrency}
              required
              placeholder={'Выберите валюту'}
              error={currencyErr}
            >
              <MenuItem value='Рубль'>Рубль</MenuItem>
              <MenuItem value='Доллар'>Доллар</MenuItem>
              <MenuItem value='Евро'>Евро</MenuItem>
              <MenuItem value='Юань'>Юань</MenuItem>
            </SelectInput>
          </Box>

          {/* Способ покупки - только для select заявок */}
          {type === 'select' && (
            <Box>
              <span>Выберите способ покупки</span>
              <SelectInput
                name={'paymentVariant'}
                value={paymentV}
                onChange={handleChangePayment}
                required
                placeholder={'Выберите способ покупки'}
                error={paymentVariantErr}
              >
                <MenuItem value='Собственные средства'>Собственные средства</MenuItem>
                <MenuItem value='Лизинг'>Лизинг</MenuItem>
              </SelectInput>
            </Box>
          )}

          {/* Лизинговые поля */}
          {type === 'leasing' && (
            <>
              <Box>
                <span>Введите стоимость предмета лизинга</span>
                <TextInput
                  onChange={handleChangePrice}
                  value={price || ''}
                  error={priceErr}
                  required
                  name={'price'}
                  type={'number'}
                  placeholder={'Введите стоимость предмета лизинга'}
                />
              </Box>
              <Box>
                <span>Введите первоначальный взнос (%)</span>
                <TextInput
                  onChange={handleChangeInitialPayment}
                  value={initialPayment || ''}
                  error={initialPaymentErr}
                  required
                  name={'initialPayment'}
                  type={'number'}
                  placeholder={'Введите первоначальный взнос (%)'}
                />
              </Box>

              <Box>
                <span>Введите срок лизинга (мес.)</span>
                <TextInput
                  onChange={handleChangeLeasePeriod}
                  value={leasePeriod || ''}
                  error={leasePeriodErr}
                  required
                  name={'leasePeriod'}
                  type={'number'}
                  placeholder={'Введите срок лизинга (мес.)'}
                />
              </Box>

              <Box>
                <span>График платежей</span>
                <SelectInput
                  name={'paymentType'}
                  value={paymentType}
                  onChange={handleChangePaymentType}
                  required
                  placeholder={'График платежей'}
                  error={paymentTypeErr}
                >
                  {paymentTypesList?.map((item: PaymentType) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.name}
                    </MenuItem>
                  ))}
                </SelectInput>
              </Box>
            </>
          )}

          {/* Количество ТС */}
          <Box>
            <span>Введите кол-во ТС</span>
            <TextInput
              onChange={handleChangeQuantity}
              value={quantity || ''}
              error={quantityErr}
              required
              name={'quantity'}
              type={'number'}
              placeholder={'Введите кол-во ТС'}
            />
          </Box>
          {/* Автомобильные поля */}
          <CarTypeBlock />
          {carTypeOtherBlock ? (
            <TextInput
              name={'otherCarType'}
              type={'text'}
              onChange={handleChangeOtherCarType}
              value={otherCarType || ''}
              placeholder={'Введите свой тип ТС'}
              required
            />
          ) : (
            <>
              <CarSubTypeBlock />
              {carSubtypeOtherBlock ? (
                <TextInput
                  name={'otherSubType'}
                  type={'text'}
                  onChange={handleChangeOtherSubType}
                  value={otherSubType || ''}
                  placeholder={'Введите свой подтип ТС'}
                  required
                />
              ) : (
                <>
                  <CarBrandBlock />
                  {carBrandOtherBlock ? (
                    <TextInput
                      name={'otherBrand'}
                      type={'text'}
                      onChange={handleChangeOtherBrand}
                      value={otherBrand || ''}
                      placeholder={'Введите свою марку'}
                      required
                    />
                  ) : (
                    <>
                      <CarModelBlock />
                      {carModelOtherBlock ? (
                        <TextInput
                          name={'otherModel'}
                          type={'text'}
                          onChange={handleChangeOtherModel}
                          value={otherModel || ''}
                          placeholder={'Введите свою модель'}
                          required
                        />
                      ) : (
                        <CarParamsBlock />
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {/* Файл */}
          <FileInputCustom
            value={kp}
            onChange={handleChangeKp}
            text={'Прикрепить файл с имеющимся предложением'}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: 0, marginTop: 3, justifyContent: 'space-between' }}>
        <ButtonCustom
          onClick={onClose}
          sx={{ backgroundColor: '#ccc', '&:hover': { backgroundColor: '#bbb' } }}
        >
          Отмена
        </ButtonCustom>
        <ButtonCustom onClick={handleSaveChanges} disabled={loading}>
          {loading
            ? 'Сохранение...'
            : appData.fulfilled
              ? 'Сохранить изменения'
              : 'Подтвердить черновик'}
        </ButtonCustom>
      </DialogActions>
    </Dialog>
  );
};
